import { ApiError } from '../../lib/api';

/*
 * Error copy for the admin company-membership actions (src/pages/Admin/CompanyDetail.tsx).
 *
 * Membership is admin-provisioned — there is no self-serve company signup and no invite flow —
 * so /admin/companies/:id is the ONLY place a recruiter seat comes to exist. An admin who cannot
 * tell why an attach or a role change failed has no second route to try.
 *
 * The remedy depends on BOTH the code and the action, because the three member routes do not
 * share an error surface. Read off the server:
 *
 *   attach  POST   /api/admin/companies/:id/members
 *           role_mismatch (adminCompanies.ts:412) · conflict (:413 :414 :419 :492 :505)
 *           not_found (:409, "No account with that email.") · validation_failed (zod)
 *           Never last_owner — adding a seat cannot strand anyone, so that route never calls
 *           assertCompanyOwnerSurvives (see its own comment at :455).
 *   role    PATCH  /api/admin/companies/:id/members/:userId
 *   remove  DELETE /api/admin/companies/:id/members/:userId
 *           last_owner (via assertCompanyOwnerSurvives, company/members.ts:57) and not_found
 *           ONLY. Both 404s carry the bare message "Not found." and mean the company row or the
 *           seat row is gone — NOT that an email was wrong. Neither route can throw conflict or
 *           role_mismatch; grep the two handlers and every AppError in them is a 404 or that one
 *           409. Copy for a code a route cannot reach is not written here.
 *
 * That split is the whole point of keying on the action: a 404 on `attach` means "nobody has
 * that email", and a 404 on `role`/`remove` means "the seat vanished underneath you". Sending
 * the second one to go check an email address is worse than saying nothing.
 *
 * `message` is the server's sentence, verbatim; `hint` is what to DO, which the sentence never
 * says. ErrorAlert renders `message` directly above `hint`, and two hints below refer to "the
 * line above" on that basis — any other consumer must keep that order or reword them.
 *
 * This is a plain .ts module rather than a const inside the .tsx page on purpose: vitest.web
 * collects only .test.ts files and runs them in a node environment with no JSX transform and no
 * jsdom (vitest.web.config.ts), so copy that lives in a .tsx file cannot be asserted on at all.
 * See companyErrorCopy.test.ts.
 */

/** Which member route the failure came from. Not cosmetic — it selects the remedy. */
export type MemberAction = 'attach' | 'role' | 'remove';

export interface MemberErrorView {
  /** The server's own sentence, verbatim — or `fallback` when the throw wasn't an ApiError. */
  message: string;
  /** What to do next. Distinct per code AND per action; null where neither carries a remedy. */
  hint: string | null;
}

// Keyed by plain strings rather than Partial<Record<ErrorCode, string>> so an unrecognised
// future code falls through to `null` instead of failing to compile here.
const MEMBER_ERROR_HINT: Record<MemberAction, Record<string, string>> = {
  attach: {
    role_mismatch:
      'Use a different email. A seat is a recruiter seat, and this account is a job seeker or an admin — no change to the account will make it eligible.',
    // Covers all four conflict sub-cases without promising a loop that cannot terminate: the
    // hard-deleted case (:492, :505) retries into a 404, so it is called out explicitly.
    conflict:
      'Resolve what the line above names — have them verify their email, re-enable the account, or check the seat it already holds — and the same address will then work. The exception is an account that no longer exists: that one was deleted, and no retry brings it back.',
    // The server already says "No account with that email.", so this adds only what it omits.
    not_found:
      'They have to sign up as a recruiter and verify their email before you can attach them.',
  },
  role: {
    last_owner:
      'A company must keep one active owner. Promote another active recruiter to owner first, then demote this one.',
    not_found:
      'This company or this seat is no longer there — it may have been removed while this page was open. Reload to see the current members.',
  },
  remove: {
    last_owner:
      'A company must keep one active owner. Promote another active recruiter to owner first, then remove this one.',
    not_found:
      'This company or this seat is no longer there — it may have been removed while this page was open. Reload to see the current members.',
  },
};

export function describeMemberError(err: unknown, action: MemberAction, fallback: string): MemberErrorView {
  if (!(err instanceof ApiError)) return { message: fallback, hint: null };
  return { message: err.message, hint: MEMBER_ERROR_HINT[action][err.code] ?? null };
}

/*
 * A 400 validation_failed carries per-input messages in `fields` and a message of literally
 * "Invalid input." (server/src/app.ts:77) — so toasting the message alone tells the admin
 * nothing. Callers render these against the input each key names.
 *
 * Only worth wiring where the form can actually produce one. The recruiter-email input can:
 * `type="email"` accepts a bare hostname ("a@b", "foo@bar") that zod's `.email()` rejects, so
 * the browser submits and the server answers 400 with `fields.email`. Measured, not assumed —
 * both strings come back REJECTED from `z.string().trim().email().max(320)`.
 */
export function readApiFieldErrors(err: unknown): Record<string, string> | null {
  if (!(err instanceof ApiError)) return null;
  return err.fields ?? null;
}
